import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { logger } from '@azure/storage-blob';
import { Box, IconButton, Typography, Stack } from '@mui/material';

import { NotificationApi } from '@/api';
import { IcClose } from '@/assets/images';
import { INotification } from '@/models';
import { useNotificationStore } from '@/store';
import { formatNotificationTime } from '@/utils/notification.utils';

import { LinkifiedText } from './LinkifiedText';

interface NotificationItemProps {
  notification: INotification;
  onNotificationClick: () => void;
}

/**
 * Format calendar event notification title
 * For CALENDAR_EVENT_ASSIGNED: [Event Name] – has been assigned to you
 * For other types: Keep original message format
 */
const formatCalendarEventTitle = (
  message: string,
  eventType: string,
  metadata?: Record<string, any>,
): string => {
  if (!metadata?.eventStartDate) {
    return message;
  }

  if (eventType === 'CALENDAR_EVENT_ASSIGNED') {
    const eventTitle = metadata.eventTitle || 'Untitled Event';
    return `${eventTitle} – has been assigned to you`;
  }

  return message;
};

/**
 * Format calendar event notification body from metadata
 * Format depends on event type:
 * - Timed event: [DD MMM HH:mm] → [DD MMM HH:mm]
 * - All-day single day: [DD MMM YYYY]
 * - All-day multi-day: [DD MMM YYYY] → [DD MMM YYYY]
 */
const formatCalendarEventBody = (metadata?: Record<string, any>): string => {
  if (!metadata) return '';

  if (metadata.acceptedSlotsLabel || metadata.requestedSlotsLabel) {
    return metadata.acceptedSlotsLabel || metadata.requestedSlotsLabel;
  }

  const isAllDay = metadata.isAllDay ?? false;

  const startDate = metadata.eventStartDate
    ? new Date(metadata.eventStartDate)
    : null;
  const endDate = metadata.eventEndDate
    ? new Date(metadata.eventEndDate)
    : null;

  if (!startDate) return '';

  // Helper to format date: "25 Nov" or "25 Nov 2025"
  const formatDate = (date: Date, includeYear = false): string => {
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      ...(includeYear ? { year: 'numeric' } : {}),
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Helper to format time: "22:00"
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Helper to format datetime: "25 Nov 22:00"
  const formatDateTime = (date: Date): string => {
    return `${formatDate(date, false)} ${formatTime(date)}`;
  };

  if (isAllDay) {
    // All-day event
    if (!endDate) {
      // Single day all-day event
      return formatDate(startDate, true);
    }

    // Compare dates to determine if single day or multi-day
    const startDay = startDate.getDate();
    const startMonth = startDate.getMonth();
    const startYear = startDate.getFullYear();

    const endDay = endDate.getDate();
    const endMonth = endDate.getMonth();
    const endYear = endDate.getFullYear();

    const isSameDay =
      startDay === endDay && startMonth === endMonth && startYear === endYear;

    if (isSameDay) {
      // Single day all-day event
      return formatDate(startDate, true);
    } else {
      // Multi-day all-day event
      return `${formatDate(startDate, true)} → ${formatDate(endDate, true)}`;
    }
  } else {
    // Timed event
    if (endDate) {
      return `${formatDateTime(startDate)} → ${formatDateTime(endDate)}`;
    } else {
      return formatDateTime(startDate);
    }
  }
};

export const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onNotificationClick,
}) => {
  const { dismissNotification } = useNotificationStore();
  const navigate = useNavigate();

  const handleDismiss = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!notification._id) return;

    try {
      await NotificationApi.dismissNotification(notification._id);
      dismissNotification(notification._id);
    } catch (error) {
      logger.error('Failed to dismiss notification:', error);
    }
  };

  const handleClick = () => {
    // Navigate to related item if needed
    if (notification.recordId && notification.recordType) {
      const recordType = notification.recordType.toLowerCase();

      if (recordType === 'case') {
        navigate(`/cases/${notification.recordId}/overview`);
      } else if (recordType === 'calendar-event') {
        navigate(`/tools/calendar-scheduler/${notification.recordId}`);
      } else if (recordType === 'task') {
        navigate(`/tasks/${notification.recordId}/view`);
      }
      onNotificationClick();
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        px: 3,
        py: 2,
        cursor: notification.recordId ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <Stack direction="row" alignItems="flex-start" marginLeft={2} spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ mb: 0.5, lineHeight: 1.4, fontWeight: 600 }}
          >
            {notification.recordType === 'calendar-event'
              ? formatCalendarEventTitle(
                  notification.message,
                  notification.eventType,
                  notification.metadata,
                )
              : notification.message}
          </Typography>

          {/* Body - Calendar event body or record ID */}
          {notification.recordType === 'calendar-event' ? (
            // Calendar event body formatted from metadata
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '0.875rem' }}
            >
              {formatCalendarEventBody(notification.metadata)}
            </Typography>
          ) : notification.recordType === 'skydio-email' ? (
            // Skydio email body: plain text with clickable URLs
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: '0.875rem', overflowWrap: 'anywhere' }}
            >
              <LinkifiedText text={notification.metadata?.emailBody || ''} />
            </Typography>
          ) : (
            // Other notification types - show record ID
            notification.recordId && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: '0.875rem' }}
              >
                {notification.recordType === 'case' ? 'Case ID: ' : 'Task ID: '}
                {notification.recordType === 'case'
                  ? notification.metadata?.caseNumber ||
                    notification.metadata?.caseId ||
                    notification.recordId
                  : notification.metadata?.taskId || notification.recordId}
              </Typography>
            )
          )}

          {/* Timestamp */}
          <Typography
            variant="caption"
            sx={{
              color: 'error.main',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {formatNotificationTime(notification.createdAt)}
          </Typography>
        </Box>

        {!notification.isDismissed && (
          <IconButton
            onClick={handleDismiss}
            size="small"
            sx={{
              color: 'text.secondary',
              mt: -0.5,
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            <IcClose width={16} height={16} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
};
